import { AccountInfo } from '@azure/msal-browser'

const PUBLIC_APP_ID = process.env.REACT_APP_PENDO_APP_ID
type PendoMethod = 'initialize' | 'identify' | 'updateOptions' | 'pageLoad' | 'track' | 'trackAgent'
type PendoAgent = {
    _q: Array<[PendoMethod, ...unknown[]]>
    initialize: (...args: unknown[]) => void
    identify: (...args: unknown[]) => void
    updateOptions: (...args: unknown[]) => void
    pageLoad: (...args: unknown[]) => void
    track: (...args: unknown[]) => void
    trackAgent: (...args: unknown[]) => void
    clearSession?: (options: { doNotTrack: boolean }) => void
}
type PendoWindow = Window & { pendo?: PendoAgent }

const PENDO_METHODS: PendoMethod[] = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent']
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

let agentPromise: Promise<PendoAgent> | undefined
let initialized = false
let currentVisitorId: string | undefined
let requestedVisitorId: string | undefined

export function getPendoVisitorId(account: AccountInfo | null): string | null {
    const claims = account?.idTokenClaims as
        | { tid?: unknown; oid?: unknown }
        | undefined
    const tid = claims?.tid
    const oid = claims?.oid
    if (typeof tid !== 'string' || typeof oid !== 'string') return null
    if (!GUID.test(tid) || !GUID.test(oid)) return null

    return `${tid.toLowerCase()}:${oid.toLowerCase()}`
}

function getPendoVisitor(account: AccountInfo | null) {
    const id = getPendoVisitorId(account)
    if (!id || !account) return null

    const claims = account.idTokenClaims as
        | { email?: unknown; given_name?: unknown; family_name?: unknown }
        | undefined
    const email = [claims?.email, account.username].find(
        value => typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()),
    ) as string | undefined
    const firstName = typeof claims?.given_name === 'string' ? claims.given_name.trim() : ''
    const lastName = typeof claims?.family_name === 'string' ? claims.family_name.trim() : ''

    return {
        id,
        ...(email && { email: email.trim() }),
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
    }
}

function loadAgent(): Promise<PendoAgent> {
    if (agentPromise) return agentPromise

    const pendoWindow = window as PendoWindow
    const pendo = (pendoWindow.pendo = pendoWindow.pendo || ({} as PendoAgent))
    pendo._q = pendo._q || []
    PENDO_METHODS.forEach(method => {
        if (!pendo[method]) {
            pendo[method] = (...args: unknown[]) => {
                pendo._q[method === 'initialize' ? 'unshift' : 'push']([
                    method,
                    ...args,
                ])
            }
        }
    })

    agentPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.async = true
        script.src = `https://cdn.eu.pendo.io/agent/static/${PUBLIC_APP_ID}/pendo.js`
        script.onload = () => {
            if (pendoWindow.pendo) resolve(pendoWindow.pendo)
            else reject(new Error('Pendo agent is unavailable'))
        }
        script.onerror = () => {
            script.remove()
            agentPromise = undefined
            reject(new Error('Pendo agent could not be loaded'))
        }
        document.head.appendChild(script)
    })

    return agentPromise
}

export async function identifyPendoVisitor(account: AccountInfo | null): Promise<boolean> {
    const visitor = getPendoVisitor(account)
    if (!visitor) {
        clearPendoIdentity()
        return false
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(PUBLIC_APP_ID || '')) {
        return false
    }

    requestedVisitorId = visitor.id
    try {
        const pendo = await loadAgent()
        if (requestedVisitorId !== visitor.id) return false

        if (!initialized) {
            pendo.initialize({ visitor, excludeAllText: true })
            initialized = true
        } else if (currentVisitorId !== visitor.id) {
            if (currentVisitorId) {
                pendo.clearSession?.({ doNotTrack: true })
            }
            pendo.identify({ visitor })
        }

        currentVisitorId = visitor.id
        return true
    } catch {
        // Analytics availability must not affect portal access or navigation.
        return false
    }
}

export function clearPendoIdentity(): void {
    const hadIdentity = Boolean(currentVisitorId)
    requestedVisitorId = undefined
    currentVisitorId = undefined
    if (initialized && hadIdentity) {
        const pendo = (window as PendoWindow).pendo
        pendo?.clearSession?.({ doNotTrack: true })
    }
}
